"use client";

import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { recordConsentDecision } from "@/lib/consent/apply";
import { useConsentStore } from "@/store/consentStore";
import Link from "next/link";

/**
 * The first-visit consent prompt.
 *
 * Shown only where consent is required, and only until a choice is made. It does
 * not block the page: refusing has to leave the product fully usable, so this is
 * a bar rather than a modal, and Escape is not a decision either way.
 *
 * Accept and Reject deliberately share a variant and a size. Making refusal the
 * quieter option is the specific thing regulators have fined companies for.
 */
export const CookieConsentBanner = () => {
  const isAsking = useConsentStore((state) => state.status === "asking");

  if (!isAsking) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="z-consent fixed inset-x-0 bottom-0 border-t border-cede-hr bg-cede-bg-component/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-cede-text-primary">Cookies</p>
          <p className="max-w-2xl text-sm text-cede-text-secondary">
            Some keep you signed in and remember your settings. Others show us how Cede Hub is used. Say no and the
            cookies behind them are never set.{" "}
            <Link
              href={ROUTES.COOKIES}
              className="text-cede-primary underline underline-offset-4 hover:text-cede-primary/80"
            >
              What we store
            </Link>
          </p>
        </div>

        <div className="flex shrink-0 gap-3">
          <Button variant="secondary" onClick={() => recordConsentDecision({ analytics: false, basis: "user" })}>
            Reject
          </Button>
          <Button variant="secondary" onClick={() => recordConsentDecision({ analytics: true, basis: "user" })}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};
